import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import {
  getStoredAuthTokens,
  getUser,
  hasExplicitLogoutMark,
  isTokenAuthEnabled,
} from "../utils/auth";
import { IS_REACT_SNAP } from "../utils/reactSnapData";

function resolveMeProbeState({ allowAnonymousProbe, forceAnonymousProbe = false }) {
  const tokenAuthEnabled = isTokenAuthEnabled();
  const { accessToken, refreshToken } = getStoredAuthTokens();
  const hasAnyToken = !!(accessToken || refreshToken);
  const cachedUser = getUser();
  const shouldAttemptMe =
    hasAnyToken ||
    !!cachedUser ||
    (!tokenAuthEnabled && (allowAnonymousProbe || forceAnonymousProbe));

  return {
    cachedUser,
    hasAnyToken,
    shouldAttemptMe,
    tokenAuthEnabled,
  };
}

export function useMe({
  allowAnonymousProbe = true,
  deferAnonymousProbe = false,
  forceAnonymousProbe = false,
} = {}) {
  const [me, setMe] = useState(() => getUser());
  const [isLoading, setIsLoading] = useState(() =>
    IS_REACT_SNAP
      ? false
      : resolveMeProbeState({ allowAnonymousProbe, forceAnonymousProbe }).shouldAttemptMe
  );
  const navigate = useNavigate();

  const load = useCallback(async ({ forceAnonymousProbe: forceProbe = forceAnonymousProbe } = {}) => {
    if (IS_REACT_SNAP) {
      setMe(getUser() || null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const { cachedUser, hasAnyToken, shouldAttemptMe, tokenAuthEnabled } =
      resolveMeProbeState({ allowAnonymousProbe, forceAnonymousProbe: forceProbe });
    try {
      if (hasExplicitLogoutMark()) {
        setMe(null);
        setIsLoading(false);
        return;
      }
      if (!shouldAttemptMe) {
        setMe(cachedUser || null);
        setIsLoading(false);
        return;
      }
      const res = await authFetch("me/", {}, navigate, {
        requireAuth: hasAnyToken, // se não houver tokens, tenta com sessão/cookie sem forçar logout
        autoLogout: false,
      });
      if (hasExplicitLogoutMark()) {
        setMe(null);
        setIsLoading(false);
        return;
      }
      if (res.status === 401 || res.status === 403) {
        if (cachedUser) {
          setMe((prev) => prev || cachedUser);
        } else {
          setMe(null);
          if (tokenAuthEnabled && hasAnyToken) {
            navigate("/login", { replace: true, state: { msg: "Sessão expirada." } });
          }
        }
        return;
      }
      if (!res.ok) {
        throw new Error(`Failed with status ${res.status}`);
      }
      const data = await res.json();
      if (hasExplicitLogoutMark()) {
        setMe(null);
        setIsLoading(false);
        return;
      }
      setMe(data);
      try {
        localStorage.setItem("user", JSON.stringify(data));
      } catch (storageError) {
        console.warn("[useMe] falha ao persistir usuário no localStorage", storageError);
      }
    } catch (err) {
      console.error("[useMe] erro ao buscar /api/me/", err);
      if (cachedUser) {
        setMe((prev) => prev || cachedUser);
      } else if (tokenAuthEnabled && hasAnyToken) {
        setMe(null);
        navigate("/login", { replace: true, state: { msg: "Sessão expirada." } });
      } else {
        setMe(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [allowAnonymousProbe, forceAnonymousProbe, navigate]);

  useEffect(() => {
    if (IS_REACT_SNAP) {
      setIsLoading(false);
      return undefined;
    }
    if (!allowAnonymousProbe && deferAnonymousProbe && !forceAnonymousProbe) {
      // Public routes can opt out of `/api/me/` entirely and rely on cached auth state.
      setIsLoading(false);
      return undefined;
    }
    load();
    return undefined;
  }, [allowAnonymousProbe, deferAnonymousProbe, forceAnonymousProbe, load]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (IS_REACT_SNAP) return undefined;
    const handleAuthCleared = () => {
      setMe(null);
      setIsLoading(false);
    };
    window.addEventListener("pm:auth:cleared", handleAuthCleared);
    return () => {
      window.removeEventListener("pm:auth:cleared", handleAuthCleared);
    };
  }, []);

  const refetch = useCallback(() => load(), [load]);

  return { me, isLoading, refetch };
}
